import { JSXElementConstructor, ReactElement, useContext } from 'react';
import {
  forwardRef,
  useStyleConfig,
  Button as ChakraButton,
  ButtonProps as ChakraButtonProps,
  Box,
  As,
} from '@chakra-ui/react';
import { ProgressCircle } from '@allergan-data-labs/alle-elements-progress-circle';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';
import { ButtonGroupContext } from './button-context';
import { getVariantStyles } from './button.theme';

export type ButtonVariants = 'solid' | 'outline' | 'ghost' | 'tinted';
export type ButtonColorScheme =
  | 'action'
  | 'brand'
  | 'muted'
  | 'positive'
  | 'negative';
export type ButtonSizes = 'lg' | 'md' | 'sm' | 'xs';

export interface ButtonProps<T = unknown> extends ChakraButtonProps {
  /**
   * The component or element to render. If none provided will render button.
   */
  as?: As;
  /**
   * The visual style of the Button.
   * @default solid
   */
  variant?: ButtonVariants;
  /** The size of the Button.
   * @default md
   */
  size?: ButtonSizes;
  /** The visual color appearance of the Button.
   * @default action
   */
  colorScheme?: ButtonColorScheme;
  /**
   * @default false
   */
  isDisabled?: boolean;
  /**
   * If `true`, the button will show a spinner and will be disabled.
   * @default false
   */
  isLoading?: boolean;
  /** The color mode that the button will display.
   * @default light
   */
  colorMode?: ColorMode;
  leftIcon?: ReactElement<T, string | JSXElementConstructor<T>>;
  rightIcon?: ReactElement<T, string | JSXElementConstructor<T>>;
}

export const getVariant = (
  prop: ButtonVariants | undefined,
  buttonGroupProp: ButtonVariants | undefined
): ButtonVariants => {
  const defaultValue: ButtonVariants = 'solid';

  return prop || buttonGroupProp || defaultValue;
};

export const getColorScheme = (
  prop: ButtonColorScheme | undefined,
  buttonGroupProp: ButtonColorScheme | undefined
): ButtonColorScheme => {
  const defaultValue: ButtonColorScheme = 'action';

  return prop || buttonGroupProp || defaultValue;
};

export const getSize = (
  prop: ButtonSizes | undefined,
  buttonGroupProp: ButtonSizes | undefined
): ButtonSizes => {
  const defaultValue: ButtonSizes = 'md';

  return prop || buttonGroupProp || defaultValue;
};

export const getColorMode = (
  prop: ColorMode | undefined,
  buttonGroupProp: ColorMode | undefined
): ColorMode => {
  const defaultValue: ColorMode = 'light';

  return prop || buttonGroupProp || defaultValue;
};

export const mapButtonSizeToProgressCircleSize = (
  size: ButtonSizes,
  isStandalone?: boolean
): 'lg' | 'md' | 'sm' | undefined => {
  switch (size) {
    case 'lg':
    case 'md':
      return 'lg';
    case 'sm':
      return 'md';
    case 'xs':
      return isStandalone ? 'sm' : 'md';
    default:
      return undefined;
  }
};

type ProgressColorScheme = 'success' | 'action' | 'brand';

export const mapButtonColorSchemeToProgressColorScheme = (
  colorScheme: ButtonColorScheme
): ProgressColorScheme => {
  switch (colorScheme) {
    case 'action':
    case 'brand':
      return colorScheme;
    case 'muted':
    default:
      return 'action';
  }
};

/**
 * Button component is used to trigger an action or event, such as submitting a form, opening a Dialog, canceling an action, or performing a delete operation.
 * Also check [useTransitionButton](?path=/docs/utilities-hooks-usetransitionbutton--docs) to handle async actions and loading states.
 */
export const Button = forwardRef<ButtonProps, typeof ChakraButton>(
  (props, ref) => {
    const {
      children,
      as,
      leftIcon,
      rightIcon,
      variant,
      size,
      isDisabled,
      isLoading,
      colorMode: propColorMode,
      colorScheme = 'action',
      ...otherProps
    } = props;
    const { colorMode: contextColorMode } = useColorMode();
    const buttonGroupProps = useContext(ButtonGroupContext);

    const buttonVariant = getVariant(variant, buttonGroupProps.variant);
    let buttonColorScheme = getColorScheme(
      colorScheme,
      buttonGroupProps.colorScheme
    );
    const buttonSize = getSize(size, buttonGroupProps.size);
    const buttonColorMode =
      propColorMode || buttonGroupProps?.colorMode || contextColorMode;

    if (variant === 'ghost' && buttonColorScheme === 'muted') {
      buttonColorScheme = 'action';
      console.warn(
        `Button Component - Muted is not a colorScheme that is allowed for ${variant} types.`
      );
    }

    const variantStyles = getVariantStyles(
      buttonVariant,
      buttonColorScheme,
      buttonColorMode
    );

    const styles = useStyleConfig('Button', {
      size: buttonSize,
    });

    if (rightIcon) {
      styles.justifyContent = 'flex-end';
    }

    const buttonStyles = Object.assign({}, styles, variantStyles);
    return (
      <ChakraButton
        ref={ref}
        as={as}
        rightIcon={rightIcon}
        leftIcon={leftIcon}
        __css={buttonStyles}
        isDisabled={isDisabled || isLoading}
        {...(isLoading ? { 'data-loading': '' } : {})}
        {...(isLoading ? { 'aria-busy': 'true' } : {})}
        {...otherProps}
      >
        {isLoading ? <Box visibility="hidden">{children}</Box> : children}
        {isLoading && (
          <Box style={{ position: 'absolute' }}>
            <ProgressCircle
              colorScheme={mapButtonColorSchemeToProgressColorScheme(
                colorScheme
              )}
              colorMode={buttonColorMode}
              size={mapButtonSizeToProgressCircleSize(buttonSize)}
            />
          </Box>
        )}
        {rightIcon && !isLoading && (
          <span style={{ display: 'flex', flex: '1' }}></span>
        )}
      </ChakraButton>
    );
  }
);
